import { XCircle, AlertCircle, CheckCircle, Send } from "lucide-react";

const TermsModal = ({
  t,
  styles,
  theme,
  language,
  isRTL,
  advancementLimits,
  termsAccepted,
  setTermsAccepted,
  setShowTermsModal,
  handleAdvancementSubmit,
  formatCurrency,
}) => {
  const bulletStyle = (isRTL) => ({
    marginBottom: "14px",
    paddingLeft: isRTL ? "0" : "24px",
    paddingRight: isRTL ? "24px" : "0",
    position: "relative",
  });

  const bulletDot = (isRTL) => ({
    position: "absolute",
    [isRTL ? "right" : "left"]: "0",
    color: "#3b82f6",
    fontWeight: "800",
  });

  return (
    <div style={styles.modalOverlay} onClick={() => setShowTermsModal(false)}>
      <div
        style={{ ...styles.modalContent, maxWidth: "700px" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>
            {language === "ar" ? "الشروط والأحكام" : "Terms & Conditions"}
          </h3>
          <button style={styles.closeButton} onClick={() => setShowTermsModal(false)}>
            <XCircle size={28} />
          </button>
        </div>

        {/* Terms Content */}
        <div
          style={{
            background:
              theme === "dark" ? "rgba(59, 130, 246, 0.12)" : "rgba(59, 130, 246, 0.08)",
            border: `2px solid ${theme === "dark" ? "rgba(59, 130, 246, 0.3)" : "rgba(59, 130, 246, 0.2)"}`,
            borderRadius: "16px",
            padding: "24px",
            marginBottom: "24px",
            maxHeight: "400px",
            overflowY: "auto",
          }}
        >
          <h4
            style={{
              fontSize: "18px",
              fontWeight: "800",
              color: theme === "dark" ? "#fff" : "#1e293b",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <AlertCircle size={24} color="#3b82f6" />
            {language === "ar" ? "قواعد وشروط السلفة" : "Advancement Rules & Conditions"}
          </h4>

          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              fontSize: "15px",
              color: theme === "dark" ? "rgba(255, 255, 255, 0.85)" : "rgba(30, 41, 59, 0.85)",
              lineHeight: 1.8,
            }}
          >
            <li style={bulletStyle(isRTL)}>
              <span style={bulletDot(isRTL)}>•</span>
              <strong>{language === "ar" ? "الحد الأقصى للسلفة:" : "Maximum Advancement:"}</strong>{" "}
              {formatCurrency(advancementLimits.maxAmount)}
              {advancementLimits.maxBySalary < advancementLimits.maxByConfig && (
                <span
                  style={{
                    display: "block",
                    fontSize: "13px",
                    color: theme === "dark" ? "rgba(255, 255, 255, 0.65)" : "rgba(30, 41, 59, 0.65)",
                    marginTop: "4px",
                  }}
                >
                  ({language === "ar" ? "محدد بنسبة" : "Limited to"}{" "}
                  {((advancementLimits.maxBySalary / advancementLimits.employeeNetSalary) * 100).toFixed(0)}%{" "}
                  {language === "ar" ? "من راتبك" : "of your salary"})
                </span>
              )}
            </li>
            <li style={bulletStyle(isRTL)}>
              <span style={bulletDot(isRTL)}>•</span>
              <strong>{language === "ar" ? "الأقساط:" : "Installments:"}</strong>{" "}
              {advancementLimits.installments?.min} - {advancementLimits.installments?.max}{" "}
              {language === "ar" ? "شهر" : "months"}
            </li>
            <li style={bulletStyle(isRTL)}>
              <span style={bulletDot(isRTL)}>•</span>
              {language === "ar"
                ? "سيتم خصم الأقساط تلقائياً من راتبك الشهري"
                : "Installments will be automatically deducted from your monthly salary"}
            </li>
            <li style={bulletStyle(isRTL)}>
              <span style={bulletDot(isRTL)}>•</span>
              {language === "ar"
                ? "يجب سداد السلفة الحالية قبل طلب سلفة جديدة"
                : "Current advancement must be repaid before requesting a new one"}
            </li>
            <li style={{ ...bulletStyle(isRTL), marginBottom: 0 }}>
              <span style={bulletDot(isRTL)}>•</span>
              {language === "ar"
                ? "جميع الطلبات تخضع للموافقة الإدارية"
                : "All requests are subject to management approval"}
            </li>
          </ul>
        </div>

        {/* Checkbox */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            padding: "20px",
            background:
              theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
            borderRadius: "16px",
            marginBottom: "28px",
            cursor: "pointer",
          }}
          onClick={() => setTermsAccepted(!termsAccepted)}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              border: `3px solid ${termsAccepted ? "#3b82f6" : theme === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.2)"}`,
              background: termsAccepted ? "#3b82f6" : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "all 0.2s ease",
            }}
          >
            {termsAccepted && <CheckCircle size={18} color="#fff" />}
          </div>
          <span
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: theme === "dark" ? "#fff" : "#1e293b",
              lineHeight: 1.5,
            }}
          >
            {language === "ar"
              ? "أوافق على الشروط والأحكام وأتعهد بسداد الأقساط في مواعيدها"
              : "I agree to the terms and conditions and commit to repaying installments on time"}
          </span>
        </div>

        {/* Actions */}
        <div style={styles.modalActions}>
          <button style={styles.secondaryButton} onClick={() => setShowTermsModal(false)}>
            {t.cancel}
          </button>
          <button
            style={{
              ...styles.primaryButton,
              opacity: termsAccepted ? 1 : 0.5,
              cursor: termsAccepted ? "pointer" : "not-allowed",
            }}
            onClick={() => {
              if (termsAccepted) {
                setShowTermsModal(false);
                handleAdvancementSubmit();
              }
            }}
            disabled={!termsAccepted}
          >
            <Send size={20} />
            {language === "ar" ? "إرسال الطلب" : "Submit Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;